<template>
    <CFormAutocomplete
        :banned="bannedValues"
        :disabled="disabled"
        :options="tags"
        :permanent="permanentValues"
        :placeholder="placeholder"
        validate
        :model-value="modelValue"
        @update:model-value="update"
        @query="updateQuery"
    />
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import CFormAutocomplete from '@/components/ui/CFormAutocomplete.vue'
import { useTypesense } from '@/composables/typesense/useTypesense'

const props = defineProps({
    modelValue: {
        type: Array as () => string[],
        default: () => []
    },
    placeholder: {
        type: String,
        default: ''
    },
    bannedValues: {
        type: Array as () => string[],
        default: () => []
    },
    permanentValues: {
        type: Array as () => string[],
        default: () => []
    },
    disabled: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['update:modelValue'])
const { loadFacets } = useTypesense()

const tags = ref<string[]>([])
const isLoading = ref(false)

const update = (value: any[]) => {
    emit('update:modelValue', value)
}

// Load interests from Typesense facets on mount
async function loadInterestsFacets() {
    if (isLoading.value) return

    isLoading.value = true
    try {
        // Load interests facets from multiple collections
        const results = await Promise.allSettled([
            loadFacets<{ interests: Record<string, number> }>('mentors', {
                facets: ['interests']
            }),
            loadFacets<{ interests: Record<string, number> }>('volunteers', {
                facets: ['interests']
            }),
            loadFacets<{ interests: Record<string, number> }>('community_members', {
                facets: ['interests']
            })
        ])

        // Merge interests from all collections
        const mergedInterests: Record<string, number> = {}

        results.forEach((result) => {
            if (result.status === 'fulfilled' && result.value.interests) {
                Object.entries(result.value.interests).forEach(([tag, count]) => {
                    mergedInterests[tag] = (mergedInterests[tag] || 0) + count
                })
            }
        })

        // Sort by count (most used first) and get the tag names
        const sortedInterests = Object.entries(mergedInterests)
            .sort((a, b) => b[1] - a[1])
            .map(([tag]) => tag)

        tags.value = sortedInterests
    } catch (error) {
        console.error('Failed to load interests facets:', error)
    } finally {
        isLoading.value = false
    }
}

const updateQuery = () => {
    // Facets are already loaded, filtering is handled by CFormAutocomplete
}

onMounted(() => {
    loadInterestsFacets()
})
</script>